import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useStoreSettings } from '../../hooks/useStoreSettings';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { FaShoppingCart, FaChevronDown, FaSearch, FaBars, FaTimes } from 'react-icons/fa';
import { supabase } from '../../lib/supabase';
import styles from './Header.module.css';
import SearchModal from '../search/SearchModal';

const CartIcon = FaShoppingCart as React.ComponentType;
const ChevronIcon = FaChevronDown as React.ComponentType;
const SearchIcon = FaSearch as React.ComponentType;
const MenuIcon = FaBars as React.ComponentType;
const CloseIcon = FaTimes as React.ComponentType;

interface Category {
  id: string;
  name: string;
  slug: string;
}

// Dodajem interfejs za HeaderContent
interface HeaderContent {
  logo: {
    url: string;
    alt: string;
    width: number;
    height?: number;
    offset_x?: number;
    offset_y?: number;
  };
  navigation: {
    id: string;
    title: string;
    url: string;
    is_active: boolean;
    selected_products?: string[];
  }[];
  displayCartIcon?: boolean;
  displaySearchIcon?: boolean;
  showBlog?: boolean;
  bg_color?: string;
  bg_opacity?: number;
  padding_y?: number;
  categoriesInline?: boolean;
}

const Header = () => {
  const { settings, loading } = useStoreSettings();
  const cartItemCount = useSelector((state: RootState) => state.cart.items.length);
  const headerContent = settings?.header_content as HeaderContent | undefined;
  const [categories, setCategories] = useState<Category[]>([]);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  // Hover-intent za dropdown kategorija: malo kasnjenje pri zatvaranju da bi
  // korisnik stigao da predje prazan prostor do menija (bez ovoga se meni
  // zatvori cim mis napusti dugme).
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const categoriesCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openCategories = () => {
    if (categoriesCloseTimer.current) clearTimeout(categoriesCloseTimer.current);
    setIsCategoriesOpen(true);
  };
  const closeCategoriesDelayed = () => {
    if (categoriesCloseTimer.current) clearTimeout(categoriesCloseTimer.current);
    categoriesCloseTimer.current = setTimeout(() => setIsCategoriesOpen(false), 280);
  };

  useEffect(() => () => {
    if (categoriesCloseTimer.current) clearTimeout(categoriesCloseTimer.current);
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      const { data } = await supabase
        .from('categories')
        .select('*')
        .eq('is_visible', true)
        .order('name');

      if (data) {
        setCategories(
          data.map(category => ({
            id: category.id,
            name: category.name,
            slug: category.slug || category.name.toLowerCase(),
          }))
        );
      }
    };

    fetchCategories();
  }, []);

  if (loading) return null;

  const defaultLogo: HeaderContent['logo'] = {
    url: '/logo.png',
    alt: 'Store Logo',
    width: 150,
    height: 50,
    offset_x: 0,
    offset_y: 0
  };

  const logo = headerContent?.logo || defaultLogo;
  const navigation = headerContent?.navigation || [];
  const activeNavItems = navigation.filter(item => item.is_active);
  const displayCartIcon = headerContent?.displayCartIcon !== false; // default to true
  const displaySearchIcon = headerContent?.displaySearchIcon === true; // default to false
  const showBlog = headerContent?.showBlog === true; // default to false
  const categoriesInline = headerContent?.categoriesInline === true;

  // Optional header background color + transparency (bg_opacity 0 = providno).
  const hexToRgba = (hex: string, a: number): string => {
    const h = hex.replace('#', '');
    const f = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
    return `rgba(${parseInt(f.slice(0, 2), 16) || 0}, ${parseInt(f.slice(2, 4), 16) || 0}, ${parseInt(f.slice(4, 6), 16) || 0}, ${a})`;
  };
  const headerStyle: React.CSSProperties = {};
  if (headerContent?.bg_color || headerContent?.bg_opacity != null) {
    const a = headerContent?.bg_opacity != null ? headerContent.bg_opacity : 1;
    headerStyle.backgroundColor = hexToRgba(headerContent?.bg_color || '#ffffff', a);
    if (a === 0) headerStyle.borderBottom = 'none';
  }
  // Visina headera kroz vertikalni padding (horizontalni 20px ostaje iz CSS-a).
  if (headerContent?.padding_y != null) {
    headerStyle.paddingTop = `${headerContent.padding_y}px`;
    headerStyle.paddingBottom = `${headerContent.padding_y}px`;
  }

  const renderNavItem = (item: typeof activeNavItems[0]) => {
    console.log('Rendering nav item:', item);
    
    if (item.id === 'categories') {
      // Horizontalni prikaz: kategorije su obicni linkovi u meniju, bez dropdown-a
      // i bez stavke "Kategorije".
      if (categoriesInline) {
        return (
          <React.Fragment key={item.id}>
            {categories.map(category => (
              <Link
                key={category.id}
                to={`/category/${category.slug}`}
                className={styles.navLink}
              >
                {category.name}
              </Link>
            ))}
          </React.Fragment>
        );
      }
      return (
        <div
          className={styles.categories}
          key={item.id}
          onMouseEnter={openCategories}
          onMouseLeave={closeCategoriesDelayed}
        >
          <span className={styles.categoryTrigger}>
            {item.title} <ChevronIcon />
          </span>
          <div
            className={`${styles.dropdown} ${isCategoriesOpen ? styles.open : ''}`}
            onClick={() => setIsCategoriesOpen(false)}
          >
            {categories.length > 0 ? (
              categories.map(category => (
                <Link
                  key={category.id}
                  to={`/category/${category.slug}`}
                  className={styles.dropdownItem}
                >
                  {category.name}
                </Link>
              ))
            ) : (
              <div className={styles.dropdownItem}>Loading categories...</div>
            )}
          </div>
        </div>
      );
    }

    return (
      <Link
        key={item.id}
        to={item.url}
        className={styles.navLink}
      >
        {item.title}
      </Link>
    );
  };

  return (
    <header ref={headerRef} className={styles.header} style={headerStyle}>
      <div className={styles.headerContent}>
        {/* Pomeraj ide kroz CSS promenljive, ne kroz inline transform — tako
            ga mobilni stil moze da ponisti (inline bi pobedio i tamo). */}
        <div
          className={styles.logoContainer}
          style={{
            '--logo-offset-x': `${logo.offset_x || 0}px`,
            '--logo-offset-y': `${logo.offset_y || 0}px`,
          } as React.CSSProperties}
        >
          <Link to="/">
            <img
              src={logo.url}
              alt={logo.alt}
              width={logo.width}
              height={logo.height || 50}
              style={{ width: `${logo.width}px`, height: 'auto', maxHeight: `${logo.height || 50}px` }}
              fetchPriority="high"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = '/default-logo.png';
              }}
            />
          </Link>
        </div>

        <nav className={styles.navigation}>
          {activeNavItems.map(item => {
            if (item.id === 'categories' && showBlog) {
              // Kod horizontalnog prikaza kategorije idu ispred Blog-a.
              return (
                <React.Fragment key={item.id}>
                  {categoriesInline ? (
                    <>
                      {renderNavItem(item)}
                      <Link to="/blog" className={styles.navLink}>Blog</Link>
                    </>
                  ) : (
                    <>
                      <Link to="/blog" className={styles.navLink}>Blog</Link>
                      {renderNavItem(item)}
                    </>
                  )}
                </React.Fragment>
              );
            }
            return renderNavItem(item);
          })}
          {showBlog && !activeNavItems.some(i => i.id === 'categories') && (
            <Link to="/blog" className={styles.navLink}>Blog</Link>
          )}
        </nav>

        <div className={styles.headerActions}>
          {displaySearchIcon && (
            <button
              className={styles.searchButton}
              onClick={() => setIsSearchModalOpen(true)}
              aria-label="Search"
            >
              <SearchIcon />
            </button>
          )}

          {displayCartIcon && (
            <Link to="/cart" className={styles.cartLink} aria-label="Korpa">
              <CartIcon />
              {cartItemCount > 0 && (
                <span className={styles.cartCount}>{cartItemCount}</span>
              )}
            </Link>
          )}

          <button
            className={styles.mobileMenuButton}
            onClick={() => setIsMobileMenuOpen(open => !open)}
            aria-label={isMobileMenuOpen ? 'Zatvori meni' : 'Otvori meni'}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className={styles.mobileMenu} onClick={() => setIsMobileMenuOpen(false)}>
          {activeNavItems.map(item => {
            if (item.id === 'categories') {
              return (
                <React.Fragment key={item.id}>
                  {showBlog && (
                    <Link to="/blog" className={styles.mobileMenuLink}>Blog</Link>
                  )}
                  <div className={styles.mobileMenuGroup}>
                    {!categoriesInline && (
                      <span className={styles.mobileMenuGroupTitle}>{item.title}</span>
                    )}
                    {categories.map(category => (
                      <Link
                        key={category.id}
                        to={`/category/${category.slug}`}
                        className={styles.mobileMenuLink}
                      >
                        {category.name}
                      </Link>
                    ))}
                  </div>
                </React.Fragment>
              );
            }
            return (
              <Link key={item.id} to={item.url} className={styles.mobileMenuLink}>
                {item.title}
              </Link>
            );
          })}
          {showBlog && !activeNavItems.some(i => i.id === 'categories') && (
            <Link to="/blog" className={styles.mobileMenuLink}>Blog</Link>
          )}
        </div>
      )}

      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
      />
    </header>
  );
};

export default Header; 